import type {
  EmittedMessage,
  IEmitterSettings,
  SerializedMessage,
  CompactQuery,
  MessageQueueItem,
} from "@whatslly/types/instant-messaging/messagesEmitter";
import type { QueryResponseType, SuccessfullQueryResponse } from "@whatslly/types/QueryResponse";
import type { PersonType } from "@whatslly/types/Person";
import { QUERY_RESPONSE_PERSON_TYPES, getPersonType } from "@whatslly/types/Person";
import type { ThreadMessageQueueItemType } from "@whatslly/types/ThreadMessageQueueItem";
import type { UserType } from "@whatslly/types/User";
import type { WhatsllyGroupType } from "@whatslly/types/WhatsllyGroup";
import type { CreateContactResponseType } from "@whatslly/types/CreateContactResponse";
import type { CreateLeadResponseType } from "@whatslly/types/CreateLeadResponse";
import { brazilPhoneVariations } from "@whatslly/commons/phoneUtils/brazilPhoneVariations";
import { isPhoneBlocked } from "@whatslly/commons/phoneUtils/blockedPhones";

export type SyncEligibleQuery = SuccessfullQueryResponse & { user: UserType } & (
    | { person: PersonType }
    | { person: PersonType[] }
    | { isGroup: true; group?: WhatsllyGroupType; person: never }
  );

export const isSyncEligibleQuery = (query: QueryResponseType | CreateContactResponseType | CreateLeadResponseType): query is SyncEligibleQuery => {
  const isBlacklisted =
    query.success &&
    (!Array.isArray(query.person)
      ? query.person?.type === QUERY_RESPONSE_PERSON_TYPES.BLACKLISTED
      : query.person.some((p) => p.type === QUERY_RESPONSE_PERSON_TYPES.BLACKLISTED));
  return Boolean(
    query.success && !isBlacklisted && (query.person || (query as SuccessfullQueryResponse).group || (query as SuccessfullQueryResponse).isGroup)
  );
};

export const shouldSyncQuery = (query: SyncEligibleQuery, isGroup: boolean | undefined) => {
  const autoSyncToDuplicateRecords = query.user.auto_sync_to_duplicate_records;
  const personArray = Array.isArray(query.person) ? query.person : [query.person];
  const isValidPerson =
    !autoSyncToDuplicateRecords && personArray.length > 1
      ? false
      : personArray.some((person) =>
          [
            QUERY_RESPONSE_PERSON_TYPES.LEAD,
            QUERY_RESPONSE_PERSON_TYPES.CONTACT,
            QUERY_RESPONSE_PERSON_TYPES.PERSON_ACCOUNT,
            QUERY_RESPONSE_PERSON_TYPES.ACCOUNT,
          ].includes(getPersonType(person) as "Contact" | "Lead" | "Person Account" | "Account")
        );

  const isValidGroup = query.user?.allowSyncGroups && isGroup;
  const hasMultipleDestinations = query.user?.reportToMultipleDestinations;
  return Boolean(isValidPerson || isValidGroup || hasMultipleDestinations);
};

export const isPhoneAllowed = (
  settings: Pick<IEmitterSettings, "phonesWhitelist" | "blockedPhones">,
  message: Pick<EmittedMessage | SerializedMessage, "receiverPhone" | "isSentByMe" | "senderPhone">
): boolean => {
  const contactPhone = message.isSentByMe ? message.receiverPhone : message.senderPhone;
  const isAllowedPhone =
    !settings.phonesWhitelist ||
    settings.phonesWhitelist?.size === 0 ||
    brazilPhoneVariations(contactPhone).some((phone) => settings.phonesWhitelist?.has(phone));
  return !isPhoneBlocked(settings.blockedPhones, message.senderPhone, message.receiverPhone) && isAllowedPhone;
};

export function getCompactQueries(query: SyncEligibleQuery): CompactQuery[] | undefined {
  if (Array.isArray(query.person)) {
    return query.person.map((person) => ({
      personId: person?.Id,
      personType: person?.type,
      recordTypeId: person?.RecordTypeId,
      wpid: person.whatsllyPersonId ?? person.whatslly__Whatslly_Person_Id__c,
      engagementId: query.engagementId,
    }));
  }
}

export function getCompactQuery(query: SyncEligibleQuery): CompactQuery | undefined {
  if (query.isGroup || query.group) {
    return {
      groupId: query.isGroup && query.group?.Id,
      groupMembers: (query.isGroup && query.group?.members) ?? undefined,
      wpid: query.whatslly_person_id,
      engagementId: query.engagementId,
    };
  }
  if (
    !Array.isArray(query.person) &&
    getPersonType(query.person) !== QUERY_RESPONSE_PERSON_TYPES.UNKNOWN &&
    getPersonType(query.person) !== QUERY_RESPONSE_PERSON_TYPES.USER
  ) {
    return {
      personId: query.person?.Id,
      personType: query.person?.type,
      recordTypeId: query.person?.RecordTypeId,
      wpid: query.whatslly_person_id,
      engagementId: query.engagementId,
    };
  }
}

export function getContactName(messages: SerializedMessage[]) {
  return messages[0].isSentByMe ? messages[0].receiverName : messages[0].senderName;
}

export function getGroupSenderName(message: SerializedMessage) {
  const contactName = message.isSentByMe ? message.receiverName : message.senderName;
  const name = message.notifyName ?? message.authorPhone ?? message.senderName;
  return name !== contactName ? name : undefined;
}

export function scrapedMessageToCRMThreadMessage(
  message: SerializedMessage,
  query: CompactQuery,
  contactPhone: string | undefined,
  tuvisCreatedById: string | undefined
): ThreadMessageQueueItemType {
  const groupSenderName = query.groupId ? getGroupSenderName(message) : undefined;
  const groupMessageMembers = query.groupMembers
    ? query.groupMembers.filter(
        (member) => member.phone === message.authorPhone || member.phone === message.senderPhone || member.phone === message.receiverPhone
      )
    : undefined;

  const threadMessage: ThreadMessageQueueItemType = {
    groupId: query.groupId,
    groupSenderName,
    groupMessageMembers,
    pid: query.personId,
    pType: query.personType,
    id: message.shortId,
    s: message.isSentByMe,
    m: message.text,
    transcriptText: message.transcriptText ? message.transcriptText.slice(0, 131072) : undefined,
    ts: message.t as number,
    p: contactPhone,
    authorPhone: message.authorPhone,
    recipientPhone: message.receiverPhone,
    senderPhone: message.senderPhone,
    wpid: query.wpid,
    latestUpdate: message.latestUpdate,
    ack: message.ack,
    quotedMsgId: message.quotedMsgId,
    templateId: message.templateId,
    tuvisCreatedById,
    isForwarded: message.isForwarded,
    isDisappearing: message.isDisappearing,
    isEdited: message.isEdited,
    chatType: message.chatType,
    location: message.location,
    isDeleted: message.isDeleted,
  };
  if (message.media?.dataURI) {
    threadMessage.fileDataURI = message.media.dataURI;
    threadMessage.fileName = message.media.fileName;
    threadMessage.fileSize = message.media.size;
  }
  if (message.media?.binary) {
    threadMessage.fileName = message.media.fileName;
    threadMessage.fileDataURI = `data:${message.media.mimeType ?? "application/octet-stream"};base64,${btoa(message.media.binary as string)}`;
    threadMessage.fileSize = message.media.size;
  }
  if (message.media?.invalid && message.media.fileName) {
    threadMessage.fileName = message.media.fileName;
    threadMessage.fileSize = message.media.size;
    threadMessage.fileInvalid = message.media.invalid;
  }

  return threadMessage;
}

export function toMessageQueueItem(
  msg: SerializedMessage,
  extra: { _id: string; contactPhone: string | undefined; query?: CompactQuery | CompactQuery[] }
): MessageQueueItem {
  if (msg.channelType === "telegram") {
    return { ...msg, ...extra };
  }
  if (msg.channelType === "whatsAppWeb") {
    return { ...msg, ...extra };
  }
  const { deferredMedia: _omit, ...rest } = msg;
  return { ...rest, ...extra };
}

export const scrapedMessageToAnalyticsMessage = ({
  message,
  query,
  isMessageSynced,
  templateId,
  uid,
}: {
  message: SerializedMessage;
  query: CompactQuery;
  isMessageSynced?: boolean;
  templateId?: string;
  uid?: string;
}) => {
  const contactPhone = message.isSentByMe ? message.receiverPhone : message.senderPhone;
  const userPhone = message.isSentByMe ? message.senderPhone : message.receiverPhone;
  return {
    schemaName: "message" as const,
    schemaContent: {
      person_id: query.personId,
      person_type: query.personType,
      record_type_id: query.recordTypeId,
      phone: contactPhone,
      message_id: message.id,
      synced: isMessageSynced,
      direction: message.isSentByMe ? "send" : "receive",
      message_type: message.type,
      message_length: message.text?.length,
      file_size: message.media?.size,
      file_type: message.media?.mimeType,
      engagement_id: query.engagementId,
      is_contact: message.isSavedContact,
      is_spam: message.isSpamContact,
      message_ts: message.timestamp ?? Date.now(),
      user_phone: userPhone,
      template_id: templateId,
      ...(uid && { user_id: uid }),
    },
  };
};
